import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { CalendarIcon, Car, Bike, Clock, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  { id: 'lavage-clean', name: 'Lavage Auto/Moto' },
  { id: 'pneumatique', name: 'Pneumatique' },
  { id: 'carrosserie', name: 'Carrosserie & Peinture' },
  { id: 'mecanique-auto', name: 'Mécanique Auto' },
  { id: 'mecanique-moto', name: 'Mécanique Moto & Scooter' },
  { id: 'climatisation', name: 'Climatisation Auto' },
  { id: 'geometrie', name: 'Géométrie & Parallélisme' },
  { id: 'vidange-revision', name: 'Vidange & Révision' },
  { id: 'freinage', name: 'Freinage' },
  { id: 'batterie-demarrage', name: 'Batterie & Démarrage' },
  { id: 'echappement', name: 'Échappement' },
  { id: 'jantes', name: 'Réparation Jantes' },
  { id: 'vitrage', name: 'Vitrage & Pare-brise' },
  { id: 'covering', name: 'Covering' },
  { id: 'preparation', name: 'Préparation Esthétique' },
  { id: 'cuir', name: 'Réparation Cuir' },
  { id: 'vitres-teintees', name: 'Vitres Teintées' },
  { id: 'controle', name: 'Contrôle Technique' },
  { id: 'depannage', name: 'Dépannage & Remorquage' },
];

export default function BookingPage() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  
  const [formData, setFormData] = useState({
    service_id: '',
    vehicle_type: 'auto',
    vehicle_brand: '',
    vehicle_model: '',
    vehicle_plate: '',
    date: null,
    time_slot: '',
    notes: ''
  });

  useEffect(() => {
    if (!authLoading && !user) {
      toast.error('Veuillez vous connecter pour prendre rendez-vous');
      navigate('/connexion');
    }
  }, [user, navigate, authLoading]);

  useEffect(() => {
    if (formData.date && formData.service_id) {
      fetchAvailableSlots();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.date, formData.service_id]);

  const fetchAvailableSlots = async () => {
    try {
      const dateStr = format(formData.date, 'yyyy-MM-dd');
      const res = await axios.get(`${API}/appointments/available-slots?date=${dateStr}&service_id=${formData.service_id}`);
      let slots = res.data.available_slots || [];
      // If the selected date is today, filter out past time slots (with 30 min buffer)
      const todayStr = format(new Date(), 'yyyy-MM-dd');
      if (dateStr === todayStr) {
        const now = new Date();
        const cutoff = now.getHours() * 60 + now.getMinutes() + 30; // 30-min buffer
        slots = slots.filter((s) => {
          const [h, m] = s.split(':').map(Number);
          return (h * 60 + m) >= cutoff;
        });
      }
      setAvailableSlots(slots);
    } catch (error) {
      console.error('Error fetching slots:', error);
    }
  };

  const handleSubmit = async (withDeposit = false) => {
    setSubmitting(true);
    try {
      const data = {
        ...formData,
        date: format(formData.date, 'yyyy-MM-dd')
      };
      if (withDeposit) {
        // Reserve creneau + acompte 5€ via SystemPay
        const res = await axios.post(
          `${API}/appointments/with-deposit`,
          { ...data, origin_url: window.location.origin },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.data?.redirect_form) {
          const { redirectToSystemPay } = await import('../lib/systempayRedirect');
          redirectToSystemPay(res.data.redirect_form);
          return;
        }
        toast.error("Erreur lors de la creation du paiement");
        return;
      }
      await axios.post(`${API}/appointments`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Rendez-vous confirmé !');
      setStep(4);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réservation');
    } finally {
      setSubmitting(false);
    }
  };

  const canProceed = () => {
    if (step === 1) return formData.service_id && formData.vehicle_type;
    if (step === 2) return formData.vehicle_brand && formData.vehicle_model && formData.vehicle_plate;
    if (step === 3) return formData.date && formData.time_slot;
    return false;
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#09090B] py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-4">
            PRENDRE <span className="text-red-500">RENDEZ-VOUS</span>
          </h1>
          <p className="text-zinc-400">Réservez votre créneau en quelques clics</p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-between mb-12 relative">
          <div className="absolute top-5 left-0 right-0 h-0.5 bg-zinc-800" />
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="relative z-10 flex flex-col items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                step >= s ? 'bg-red-600 text-white' : 'bg-zinc-800 text-zinc-500'
              }`}>
                {step > s ? <CheckCircle size={20} /> : s}
              </div>
              <span className={`mt-2 text-sm ${step >= s ? 'text-white' : 'text-zinc-500'}`}>
                {s === 1 && 'Service'}
                {s === 2 && 'Véhicule'}
                {s === 3 && 'Date'}
                {s === 4 && 'Confirmé'}
              </span>
            </div>
          ))}
        </div>

        {/* Step 1: Service */}
        {step === 1 && (
          <Card className="bg-zinc-900/50 border-zinc-800 animate-fade-in">
            <CardHeader>
              <CardTitle className="text-white">Choisissez votre service</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label className="text-zinc-300">Type de véhicule</Label>
                <div className="flex gap-4 mt-2">
                  <Button
                    type="button"
                    variant={formData.vehicle_type === 'auto' ? 'default' : 'outline'}
                    className={formData.vehicle_type === 'auto' ? 'bg-red-600' : 'border-zinc-700'}
                    onClick={() => setFormData({...formData, vehicle_type: 'auto'})}
                    data-testid="vehicle-type-auto"
                  >
                    <Car className="mr-2" size={18} />
                    Auto
                  </Button>
                  <Button
                    type="button"
                    variant={formData.vehicle_type === 'moto' ? 'default' : 'outline'}
                    className={formData.vehicle_type === 'moto' ? 'bg-red-600' : 'border-zinc-700'}
                    onClick={() => setFormData({...formData, vehicle_type: 'moto'})}
                    data-testid="vehicle-type-moto"
                  >
                    <Bike className="mr-2" size={18} />
                    Moto
                  </Button>
                </div>
              </div>

              <div>
                <Label className="text-zinc-300">Service souhaité</Label>
                <Select 
                  value={formData.service_id} 
                  onValueChange={(v) => setFormData({...formData, service_id: v})}
                >
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white mt-2" data-testid="service-select">
                    <SelectValue placeholder="Sélectionnez un service" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {SERVICES.map((service) => (
                      <SelectItem key={service.id} value={service.id} className="text-white">
                        {service.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button 
                className="w-full bg-red-600 hover:bg-red-700"
                disabled={!canProceed()}
                onClick={() => setStep(2)}
                data-testid="step1-next"
              >
                Continuer
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Vehicle */}
        {step === 2 && (
          <Card className="bg-zinc-900/50 border-zinc-800 animate-fade-in">
            <CardHeader>
              <CardTitle className="text-white">Informations véhicule</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-zinc-300">Marque</Label>
                <Input
                  placeholder="Ex: Renault, BMW, Honda..."
                  value={formData.vehicle_brand}
                  onChange={(e) => setFormData({...formData, vehicle_brand: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white mt-2"
                  data-testid="vehicle-brand"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Modèle</Label>
                <Input
                  placeholder="Ex: Clio, Serie 3, CBR..."
                  value={formData.vehicle_model}
                  onChange={(e) => setFormData({...formData, vehicle_model: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white mt-2"
                  data-testid="vehicle-model"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Immatriculation</Label>
                <Input
                  placeholder="Ex: AB-123-CD"
                  value={formData.vehicle_plate}
                  onChange={(e) => setFormData({...formData, vehicle_plate: e.target.value.toUpperCase()})}
                  className="bg-zinc-950 border-zinc-800 text-white mt-2"
                  data-testid="vehicle-plate"
                />
              </div>

              <div className="flex gap-4 pt-4">
                <Button variant="outline" className="flex-1 border-zinc-700" onClick={() => setStep(1)}>
                  Retour
                </Button>
                <Button 
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  disabled={!canProceed()}
                  onClick={() => setStep(3)}
                  data-testid="step2-next"
                >
                  Continuer
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Date & Time */}
        {step === 3 && (
          <Card className="bg-zinc-900/50 border-zinc-800 animate-fade-in">
            <CardHeader>
              <CardTitle className="text-white">Choisissez votre créneau</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label className="text-zinc-300">Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal bg-zinc-950 border-zinc-800 text-white mt-2"
                      data-testid="date-picker-trigger"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.date ? format(formData.date, 'PPP', { locale: fr }) : 'Sélectionnez une date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-800">
                    <Calendar
                      mode="single"
                      selected={formData.date}
                      onSelect={(date) => setFormData({...formData, date, time_slot: ''})}
                      disabled={(date) => {
                        // Allow today and future dates (working days only — Sunday closed)
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return date < today || date.getDay() === 0;
                      }}
                      initialFocus
                      className="bg-zinc-900"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {formData.date && (
                <div>
                  <Label className="text-zinc-300">Créneau horaire</Label>
                  <div className="grid grid-cols-4 gap-2 mt-2">
                    {availableSlots.length > 0 ? (
                      availableSlots.map((slot) => (
                        <Button
                          key={slot}
                          variant={formData.time_slot === slot ? 'default' : 'outline'}
                          className={formData.time_slot === slot ? 'bg-red-600' : 'border-zinc-700 text-zinc-300'}
                          onClick={() => setFormData({...formData, time_slot: slot})}
                          data-testid={`slot-${slot}`}
                        >
                          <Clock size={14} className="mr-1" />
                          {slot}
                        </Button>
                      ))
                    ) : (
                      <p className="col-span-4 text-zinc-500 text-center py-4">
                        Aucun créneau disponible pour cette date
                      </p>
                    )}
                  </div>
                </div>
              )}

              <div>
                <Label className="text-zinc-300">Notes (optionnel)</Label>
                <Textarea
                  placeholder="Informations supplémentaires..."
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white mt-2"
                  data-testid="booking-notes"
                />
              </div>

              <div className="flex flex-col gap-3 pt-4">
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                  <p className="text-amber-300 text-sm">
                    Vous pouvez securiser votre creneau avec un <strong>acompte de 5€</strong> paye en ligne, ou simplement reserver gratuitement et regler en magasin.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button variant="outline" className="border-zinc-700" onClick={() => setStep(2)}>
                    Retour
                  </Button>
                  <Button 
                    variant="outline"
                    className="flex-1 border-zinc-700"
                    disabled={!canProceed() || submitting}
                    onClick={() => handleSubmit(false)}
                    data-testid="confirm-booking"
                  >
                    {submitting ? 'Confirmation...' : 'Reserver sans acompte'}
                  </Button>
                  <Button 
                    className="flex-1 bg-amber-600 hover:bg-amber-700"
                    disabled={!canProceed() || submitting}
                    onClick={() => handleSubmit(true)}
                    data-testid="confirm-booking-deposit"
                  >
                    {submitting ? '...' : 'Acompte 5€ (securise)'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Confirmation */}
        {step === 4 && (
          <Card className="bg-zinc-900/50 border-zinc-800 animate-fade-in text-center">
            <CardContent className="py-12">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="text-green-500" size={40} />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Rendez-vous Confirmé !</h2>
              <p className="text-zinc-400 mb-2">
                {SERVICES.find(s => s.id === formData.service_id)?.name}
              </p>
              <p className="text-zinc-400 mb-2">
                {formData.vehicle_brand} {formData.vehicle_model} ({formData.vehicle_plate})
              </p>
              <p className="text-red-500 font-bold text-lg mb-8">
                {formData.date && format(formData.date, 'EEEE d MMMM yyyy', { locale: fr })} à {formData.time_slot}
              </p>
              <p className="text-zinc-500 mb-8">
                Un email de confirmation vous a été envoyé. Vous avez gagné 10 points de fidélité !
              </p>
              <div className="flex justify-center gap-4">
                <Button onClick={() => navigate('/mon-compte')} className="bg-red-600 hover:bg-red-700">
                  Voir mes RDV
                </Button>
                <Button variant="outline" className="border-zinc-700" onClick={() => navigate('/')}>
                  Retour à l'accueil
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
